import { splitLatexSegments } from "./latexSegments"
import { latexToUnicode, rescueLooseLatex } from "./latexToUnicode"

/**
 * Découpe un texte en unités de dévoilement pour l'apparition progressive.
 *
 * La prose se dévoile caractère par caractère, une formule d'un seul bloc :
 * dévoiler `\frac{3}{4}` lettre à lettre afficherait de la syntaxe cassée puis
 * sauterait d'un coup. C'est la contrainte que Wajih avait anticipée.
 *
 * Deux propriétés voulues :
 *
 * - **inerte sans maths** : un texte sans formule rend exactement un jeton par
 *   caractère, donc l'animation, les délais et les rappels du `TypeWriter`
 *   restent identiques au caractère près. C'est ce qui rend l'évolution sûre
 *   pour un composant partagé par tous les champions.
 * - **découpage par point de code** : `for…of` respecte les paires de
 *   substitution, là où `text[i]` coupait un emoji en deux moitiés invalides.
 *
 * Le rendu des formules est ici la composition Unicode et non KaTeX : la bulle
 * d'un champion est un `<Text>` animé, on ne peut pas y injecter du HTML sans
 * casser le dévoilement. Pour des maths de collège, c'est un bon échange.
 *
 * @param {string} text
 * @returns {Array<{type:"char"|"math", value:string, display?:boolean}>}
 */
export function toRevealTokens(text) {
    if (!text || typeof text !== "string") return []

    const tokens = []
    for (const segment of splitLatexSegments(text)) {
        if (segment.type === "math") {
            tokens.push({
                type: "math",
                value: latexToUnicode(segment.value),
                display: segment.display,
            })
            continue
        }
        // Même filet que dans `RichText` : une formule oubliée hors
        // délimiteurs se dévoile en Unicode plutôt qu'en antislashs.
        for (const char of rescueLooseLatex(segment.value)) {
            tokens.push({ type: "char", value: char })
        }
    }
    return tokens
}

/** Le texte d'un jeton, pour les rappels qui attendent une chaîne. */
export function tokenValue(token) {
    return typeof token === "string" ? token : (token?.value ?? "")
}
